import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import { chartTypes, interpolate } from '@aytm/app/insights/dashboard/lib/charts'
import { defaultColors } from '@aytm/app/insights/dashboard/lib/colors'
import { isRowAveragesUid } from '@aytm/app/insights/dashboard/lib/config'

import { useAttributes, useValue } from '@aytm/app/insights/dashboard/components/hooks'

import { renderChart, seriesName, showCollapsedSections, showAttributeMetrics, showUniqueAttributes } from '../config'
import { HamburgerIcon } from '../HamburgerIcon'
import { safelyExecuteSync } from '@aytm/app/insights/dashboard/lib/safety'

import { stateSelector, shouldUpdate } from './selectors'

import './DataChart.css'

const CLASS = 'insights-portal-data-chart'

const DataChart = props => {
  /** Get attribute captions */
  const { attributes, getCaptions } = useAttributes()

  const [fullScreen, setFullScreen] = useState(false)

  const onChartTypeChange = props.onChartTypeChange || null

  const { isSharedView, data, dataChartType, dataChartScale, sortByAttribute, sortByMethod } = usePool(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  /** Chart type */
  const chartType = useMemo(() => chartTypes.find(v => dataChartType === v.id) || chartTypes[0], [dataChartType])

  const items = useMemo(() => props.items || [], [props.items])

  /** getColumnData function */
  const { getColumnData, getCellData } = useValue()

  const chart = useRef()
  const chartContainer = useRef()
  const chartContainerID = useRef()
  if (!chartContainerID.current) {
    chartContainerID.current = `insights-chart-${Date.now().toString(32)}${Math.floor(Math.random() * 65536).toString(
      32
    )}`
  }

  const series = useMemo(() => {
    // Create captions for attributes in all sections
    const captions = getCaptions({
      showSections: false,
      showMetrics: showAttributeMetrics,
      showMetadata: false,
      showCollapsed: showCollapsedSections,
      showUnique: showUniqueAttributes
    })
    // Create data series
    const result =
      items && data && captions?.length
        ? items
            .filter(item => item.uid && !isRowAveragesUid(item.uid))
            .reduce((acc, item) => {
              for (const [
                attributeIndex,
                { section, attribute, metrics, defaultMetrics, disabled }
              ] of captions.entries()) {
                const selected = attribute === sortByAttribute && (!metrics?.id || metrics?.id === sortByMethod)
                const ser = acc.find(
                  v =>
                    section === v.custom.section &&
                    attribute === v.custom.attribute &&
                    (!metrics?.id || metrics?.id === v.custom.metrics?.id)
                ) || {
                  name: seriesName(attributes, attribute, metrics),
                  custom: {
                    section,
                    attribute,
                    metrics
                  },
                  data: [],
                  lineWidth: selected ? 3 : 1,
                  color: defaultColors[attributeIndex % defaultColors.length],
                  shadow: selected
                    ? {
                        color: defaultColors[attributeIndex % defaultColors.length],
                        width: 7,
                        offsetX: 0,
                        offsetY: 0,
                        opacity: 0.25
                      }
                    : false,
                  marker: { symbol: 'circle', radius: 7 },
                  zIndex: selected ? 1 : 0,
                  visible: disabled.includes(metrics?.id) ? false : true
                  // connectNulls: true
                }
                if (!ser.data.length) {
                  acc.push(ser)
                }
                const { text } = getColumnData(item)
                const { value } = getCellData(item, attribute, metrics, defaultMetrics)
                if (null == value || Number.isNaN(value)) {
                  ser.data.push([text, null])
                } else {
                  ser.data.push([text, Math.round(100 * value || 0) / 100])
                }
              }
              return acc
            }, [])
        : []
    // Interpolate data if needed
    return ['line', 'area'].includes(chartType.type) ? result.map(v => interpolate(v)) : result
  }, [getCaptions, items, data, chartType.type, sortByAttribute, sortByMethod, attributes, getColumnData, getCellData])

  useEffect(() => {
    if (!fullScreen) {
      chart.current = safelyExecuteSync(
        () =>
          renderChart({
            id: chartContainerID.current,
            series,
            paddingLeft: props.paddingLeft,
            blockWidth: props.blockWidth,
            scale: dataChartScale,
            chartType,
            setChartType: event => onChartTypeChange?.({ type: event.id }),
            setFullScreen: setFullScreen,
            isSharedView
          }),
        chart.current || null
      )
    }
  }, [
    chartType,
    dataChartScale,
    fullScreen,
    isSharedView,
    onChartTypeChange,
    props.blockWidth,
    props.paddingLeft,
    series
  ])

  const className = props.className || CLASS

  // return <div className={className} style={props.style} />

  if (!series?.length) return null

  return (
    <div className={className} style={props.style}>
      <div className="export-button-container">
        <button className="export-button" type="button">
          <HamburgerIcon />
        </button>
      </div>
      <div id={chartContainerID.current} ref={chartContainer} />
    </div>
  )
}

DataChart.propTypes = {
  wait: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object),
  timeout: PropTypes.number,
  paddingLeft: PropTypes.number.isRequired,
  columnWidth: PropTypes.number.isRequired,
  blockWidth: PropTypes.number,
  onChartTypeChange: PropTypes.func
}

DataChart.defaultProps = {
  wait: false,
  items: [],
  timeout: 700
}

const propsAreEqual = (prev, cur) =>
  !cur.wait &&
  prev.items === cur.items &&
  prev.className === cur.className &&
  prev.style === cur.style &&
  prev.paddingLeft === cur.paddingLeft &&
  prev.blockWidth === cur.blockWidth &&
  prev.onChartTypeChange === cur.onChartTypeChange

export default React.memo(DataChart, propsAreEqual)
